/* eslint-disable @nx/enforce-module-boundaries */
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Textarea,
} from '@mybridge/ui';
import React, { forwardRef, useContext, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MyProfileContext } from '../context';
import { DateSelect } from 'v4/components/date-select';
import { useDisclosure } from '@mybridge/ui';
import { CreateAudiencePermissionModal } from '../audience-modal-create';
import { EarthGlobeIcon, OnlyMeLockIcon, TeamIcon } from '@mybridge/icons';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { Text } from '@chakra-ui/react';
export const MyProfileMilestoneModal = React.memo(forwardRef(({ ...props }, ref) => {
  const {
    milestonesDisc,
    addMilestone,
    addMilestoneLoading,
    selectedMilestone,
    updateMilestone,
    setSelectedMilestone,
    removeMilestone,
    removeMilestoneLoading,
    isDateAfter,
  } = useContext(MyProfileContext);
  const { t } = useTranslator();
  const audienceCreateDisc = useDisclosure();
  const [audienceValue, setAudienceValue] = useState(1);
  const milestone = {
    ...(selectedMilestone ?? {}),
    start_date: {
      month: selectedMilestone?.month,
      year: selectedMilestone?.year,
    },
  };

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: milestone ?? {},
  });

  // console.log(milestone);

  const onSubmit = async (form) => {
    const payload = {
      ...(form ?? {}),
      month: form?.start_date?.month,
      year: form?.start_date?.year,
      audience: audienceValue,
    };
    if (selectedMilestone?.id) {
      await updateMilestone({
        id: selectedMilestone?.id,
        ...(payload ?? {}),
      });
      setSelectedMilestone();
    } else {
      await addMilestone(payload);
    }
  };

  const onClose = () => {
    milestonesDisc?.onClose();
    setSelectedMilestone?.(null);
  };

  const renderPermissionSwitch = (param) => {
    switch (param) {
      case 1:
        return {
          icon: <EarthGlobeIcon width="16px" height="16px" />,
          text: t('profileDetails.public') || 'Public',
        };
      case 2:
        return {
          icon: <TeamIcon width="16px" height="16px" />,
          text: t('profileDetails.myNetwork') || 'myNetwork',
        };
      case 3:
        return {
          icon: <OnlyMeLockIcon width="16px" height="16px" />,
          text: t('profileDetails.onlyMe') || 'Only me',
        };
      default:
        return {
          icon: <EarthGlobeIcon width="16px" height="16px" />,
          text: t('profileDetails.public') || 'Public',
        };
    }
  };

  return (
    <>
      {' '}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Heading
          fontSize="18px"
          color="brandPrimary.500"
          fontWeight="400"
          mb={4}
        >
          {t('profileMilestones.milestones') || 'Milestones'}
        </Heading>
        {/* <ModalBody> */}
        <Stack spacing={4}>
          <FormControl isInvalid={errors?.start_date}>
            <FormLabel>{t('profileMilestones.date') || 'Date'}*</FormLabel>
            <Controller
              control={control}
              render={({ field }) => (
                <DateSelect variant="floating" flex={1} {...field} />
              )}
              rules={{ required: true }}
              name="start_date"
            />
            <FormErrorMessage>{t('profileMilestones.dateRequired') || 'Date is required'}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={errors?.title} variant="floating">
            <Input
              defaultValue={''}
              {...register('title', { required: true })}
              placeholder=""
              sx={{ textTransform: 'capitalize' }}
            />
            <FormLabel>{t('profileMilestones.title') || 'Title'}*</FormLabel>
            <FormErrorMessage>{t('profileMilestones.titleRequired') || 'Title is required'}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={errors?.milestone} variant="floating">
            <Textarea
              {...register('milestone', { 
                required: false,
                maxLength: {
                  value: 250,
                  message: 'Description must be 250 characters or less'
                }
              })}
              variant="outline"
              placeholder=""
              maxLength={250}
            />
            <FormLabel>{t('profileMilestones.description') || 'Description'}</FormLabel>
            <Text
              color="#5B5B5B"
              fontSize="12px"
              textAlign="right"
              fontWeight={400}
            >
              {watch('milestone')?.length || 0}/250
            </Text>
            {watch('milestone')?.length >= 250 && (
              <Text mb={2} fontSize="14px" color="red.500">
                {' '}
                {'Description must be 250 characters or less'}
              </Text>
            )}
            <FormErrorMessage>{t('profileMilestones.descriptionRequired') || 'Description is required'}</FormErrorMessage>
          </FormControl>
        </Stack>
        {/* </ModalBody>
        <ModalFooter> */}
        <HStack flex={1} mt={4}>
          <HStack flex={1} justifyContent="flex-start">
            <Button
              borderRadius="50px"
              leftIcon={renderPermissionSwitch(audienceValue)?.icon}
              type="button"
              onClick={() => {
                audienceCreateDisc.onOpen();
              }}
            >
              {renderPermissionSwitch(audienceValue)?.text}
            </Button>
            {selectedMilestone?.id ? (
              <Button
                onClick={(e) => removeMilestone(selectedMilestone)}
                isLoading={removeMilestoneLoading}
                color="red.500"
                variant="ghost"
                borderRadius="50px"
              >
                {t('buttons.delete') || 'Delete'}
              </Button>
            ) : (
              ''
            )}
          </HStack>

          <HStack justifyContent="flex-end" flex={1}>
            <Button
              onClick={(e) => {milestonesDisc?.onClose?.();setSelectedMilestone();}}
              variant="secondary"
              borderRadius="50px"
            >
              {t('buttons.cancel') || 'Cancel'}
            </Button>
            <Button
              isLoading={addMilestoneLoading}
              type="submit"
              variant="primary"
              borderRadius="50px"
            >
              {t('buttons.save') || 'Save'}
            </Button>
          </HStack>
        </HStack>
        {/* </ModalFooter> */}
      </form>
      {audienceCreateDisc?.isOpen ? (
        <CreateAudiencePermissionModal
          {...audienceCreateDisc}
          setPermissionValue={(e) => setAudienceValue(e)}
        />
      ) : (
        ''
      )}
    </>
  );
}));
